import React, {
  Fragment,
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import axios from 'axios';
import Image from 'next/image';
import { CartContext } from 'pages/cart';
import cn from 'classnames';
import { specificationsToStr } from '@lambda/reebelo/src/commonHelpers';
import Loading from '@/components/Loading/Loading';
import { CtaButton } from '@/components/Cta/Cta';
import close from '@/public/icons/account/close.svg';
import { priceFormater } from '@/settings';
import {
  ItemTypeWithProps,
  RecommendedItemSlideOverProps,
  RecommendedItemSlideOverInnerProps,
} from './types';
import i18n from '@/settings/i18n';
import useAttributes from '@/lib/use-attributes';
import { handleAddToCart, handleQtyChange } from './recommendedItemsUtils';
import { generateCategoryAttr } from '@/lib/use-cart';
import { getComparePrice } from '@/lib/use-pim-compare-price';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { ParseMarkdown } from '../markdown';

const t = i18n.init();

const RecommendedItemSlideOverInner = ({
  isProductLoading,
  lineItem,
  product,
  pimProduct,
  quantity,
  setOpen,
}: RecommendedItemSlideOverInnerProps) => {
  const {
    addCartDateTimeAttribute,
    deliveryAttributes,
    shippingAttributes,
    subsidyAttributes,
    vendorAttributes,
    offerUppricerMetaAttributes,
    categoryPkAttributes,
  } = useAttributes({ product, type: 'offer' } as ItemTypeWithProps);

  const { add, loading, setScrollPosOnCartChange, update } =
    useContext(CartContext);

  const handleClickAddToCart = () => {
    if (quantity && quantity > 0 && lineItem) {
      const handleQtyChangeFn = handleQtyChange({
        update,
        product,
        price: product.price,
        lineId: lineItem.id,
        availableQty: product.stock,
        lineItem,
        accessoryModalSource: true,
      });

      handleQtyChangeFn(quantity + 1);
    } else {
      handleAddToCart({
        add,
        loading,
        variantId: product?.variantId,
        type: 'offer',
        title: product.title,
        price: product.price,
        vendor: product.vendor,
        setScrollPosOnCartChange,
        attributes: [
          ...vendorAttributes,
          ...subsidyAttributes,
          ...shippingAttributes,
          ...deliveryAttributes,
          ...addCartDateTimeAttribute,
          ...generateCategoryAttr(
            product.productType || product.categories?.[0],
          ),
          ...offerUppricerMetaAttributes,
          ...categoryPkAttributes,
        ],
        quantity: 1,
        itemCondition: product.condition,
        itemColor: product.color,
        itemBrand: product.brand,
        itemCategory: product.categories ? product.categories.join('|') : '',
        itemStorage: product.storage,
        itemPsku: product.psku,
        itemStock: product.stock,
        accessoryModalSource: true,
      })();
    }

    setOpen(false);
  };

  // These are used to determine if the "read more" button and an overlay should be shown
  // over the description text
  const descriptionContainerElement = useRef<HTMLParagraphElement | null>(null);
  const descriptionElement = useRef<HTMLParagraphElement | null>(null);
  const [isDescriptionElementOverflowing, setIsDescriptionElementOverflowing] =
    useState(false);

  useEffect(() => {
    if (
      descriptionElement.current?.clientHeight &&
      descriptionContainerElement.current?.clientHeight
    ) {
      setIsDescriptionElementOverflowing(
        descriptionElement.current?.clientHeight >
          descriptionContainerElement.current.clientHeight,
      );
    }
  }, [descriptionContainerElement.current, descriptionElement.current]);

  const [readMore, setReadMore] = useState(false);

  // Will reset readMore on product change
  // e.g. the user clicks another recommended offer without closing the slide-over
  useEffect(() => setReadMore(false), [product]);

  const [material, setMaterial] = useState<string | undefined>();
  const [comparePrice, setComparePrice] = useState<number | undefined>();

  const { color } = product;

  useEffect(() => {
    if (!pimProduct) return;

    const { colors, colorsMaterial } = pimProduct;

    // The available colors are in the pimProduct like { BLK: "Black", WHT: "White" }
    // We need the key of the color to get the material
    const colorKey = Object.keys(colors).find((key) => colors[key] === color);

    if (!colorKey) return;

    // The materials are listed by color key in the pimProduct like { BLK: "Silicone", WHT: "Silicone" }
    const colorMaterial = colorsMaterial ? colorsMaterial[colorKey] : undefined;

    setMaterial(colorMaterial);

    const comparePriceResult = getComparePrice(pimProduct, product);

    setComparePrice(comparePriceResult);
  }, [pimProduct]);

  const parseMarkdownVars = {
    country: getShortenCountryName(),
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="my-10 flex w-full flex-row items-center justify-center px-4 sm:px-6">
        <Image
          width={300}
          height={300}
          alt={product.title}
          src={product.imageSrc}
          className="h-full w-full object-contain"
        />
      </div>
      <div className="grow bg-gray-200 px-4 py-6 sm:px-6">
        <h1 className="text-sm font-bold tracking-wide">
          {product.title.split(' - ')[0]}
        </h1>
        <div className="flex justify-start">
          <h1 className="text-sm font-semibold">
            {priceFormater.long(product.price)}
          </h1>
          {comparePrice && (
            <p className="ml-2 text-xs text-gray-900 line-through">
              {priceFormater.long(comparePrice)}
            </p>
          )}
        </div>
        {isProductLoading ? (
          <div className="flex h-full max-h-[50vh] items-center justify-center py-5">
            <Loading fill="none" />
          </div>
        ) : (
          <>
            {(color || material) && (
              <div className="mt-9 flex w-full flex-row justify-start">
                {color && (
                  <div className="w-1/4 text-sm">
                    <p>COLOR</p>
                    <p className="font-semibold">{color}</p>
                  </div>
                )}
                {material && (
                  <div className="w-1/4 text-sm">
                    <p>MATERIAL</p>
                    <p className="font-semibold">{material}</p>
                  </div>
                )}
              </div>
            )}
            <div className="my-9 w-full">
              <CtaButton
                variant="dark"
                onClick={handleClickAddToCart}
                id="e2e-recommended-offer-slideover-add-to-cart-btn"
                className="pointer-events-auto my-3 block w-full p-3 font-bold"
              >
                {t`Add to Cart`}
              </CtaButton>
            </div>
            <div
              ref={descriptionContainerElement}
              className={cn('[&>ul]:list-disc grow text-xs', {
                'relative h-64 overflow-hidden md:h-72': !readMore,
                'before:absolute before:left-0 before:top-36 before:h-full before:w-full before:bg-gradient-to-b before:from-transparent before:to-gray-200':
                  !readMore && isDescriptionElementOverflowing,
              })}
            >
              {pimProduct?.description && (
                <p ref={descriptionElement}>
                  <ParseMarkdown
                    markdown={pimProduct.description}
                    variables={parseMarkdownVars}
                    size="small"
                  />
                </p>
              )}
              {!pimProduct?.description && pimProduct?.specifications && (
                <p
                  ref={descriptionElement}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html:
                      specificationsToStr(pimProduct.specifications) || '',
                  }}
                ></p>
              )}
            </div>
            {!readMore && isDescriptionElementOverflowing && (
              <div
                className="mt-2 w-full cursor-pointer text-center text-sm text-gray-700"
                onClick={() => setReadMore(true)}
              >
                <p className="font-semibold">Read More</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const RecommendedItemSlideOver = ({
  afterCloseFn,
  lineItem,
  open,
  product,
  quantity,
  setOpen,
}: RecommendedItemSlideOverProps) => {
  const [pimProduct, setPimProduct] = useState<PimProductT | undefined>();
  const [isProductLoading, setIsProductLoading] = useState(true);

  const getPimProduct = async () => {
    if (product?.psku) {
      const response = await axios.get<PimProductT>(
        `api/products/get-product?psku=${product.psku}`,
      );

      setPimProduct(response.data);
    }

    setIsProductLoading(false);
  };

  useEffect(() => {
    setIsProductLoading(true);
    getPimProduct();
  }, [product]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="pointer-events-none relative z-80"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
                beforeEnter={getPimProduct}
                afterLeave={() => {
                  setPimProduct(undefined);
                  afterCloseFn();
                }}
              >
                <Dialog.Panel className="pointer-events-auto w-full md:w-[512px] 2xl:w-[672px]">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-3xl">
                    <div className="ml-3 flex h-7 flex-row items-center justify-end px-4 pt-6 sm:px-6">
                      <button
                        type="button"
                        className="pointer-events-auto relative rounded-md bg-white text-gray-400 hover:text-gray-700 focus:outline-none"
                        onClick={() => setOpen(false)}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <Image width={16} height={16} alt="close" src={close} />
                      </button>
                    </div>
                    <Dialog.Panel className="h-full">
                      {product && (
                        <RecommendedItemSlideOverInner
                          isProductLoading={isProductLoading}
                          lineItem={lineItem}
                          product={product}
                          pimProduct={pimProduct}
                          quantity={quantity}
                          setOpen={setOpen}
                        />
                      )}
                    </Dialog.Panel>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
